import { Button } from '../../components/ui';
import { SectionCard } from '../../components/trainer/SectionCard';

interface ClientSummary {
  id: string;
  displayName: string;
  phone: string;
  status: 'pending' | 'linked' | 'declined';
}

interface ClientsOverviewProps {
  clients: ClientSummary[];
  linkedCount: number;
  pendingCount: number;
  onViewAllClients: () => void;
}

const statusLabel: Record<ClientSummary['status'], string> = {
  linked: 'Linked',
  pending: 'Pending',
  declined: 'Declined',
};

const statusClassName: Record<ClientSummary['status'], string> = {
  linked: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200',
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-200',
  declined: 'bg-slate-200 text-slate-700 dark:bg-white/10 dark:text-slate-300',
};

export function ClientsOverview({
  clients,
  linkedCount,
  pendingCount,
  onViewAllClients,
}: ClientsOverviewProps) {
  const previewClients = clients.slice(0, 4);

  return (
    <SectionCard
      eyebrow="Clients"
      title="Clients Overview"
      action={
        <Button variant="ghost" size="sm" onClick={onViewAllClients}>
          View all
        </Button>
      }
      fillHeight
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 dark:border-white/10 dark:bg-white/5">
          <p className="text-xs uppercase tracking-wide text-gray-600 dark:text-slate-400">Linked clients</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">{linkedCount}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 dark:border-white/10 dark:bg-white/5">
          <p className="text-xs uppercase tracking-wide text-gray-600 dark:text-slate-400">Pending invites</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">{pendingCount}</p>
        </div>
      </div>

      <div className="mt-4 space-y-3">
        {previewClients.length > 0 ? (
          previewClients.map((client) => (
            <div
              key={client.id}
              className="rounded-xl border border-gray-200 bg-white/60 px-4 py-3 dark:border-white/10 dark:bg-white/5"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">{client.displayName}</p>
                  <p className="text-sm text-gray-600 dark:text-slate-400">{client.phone}</p>
                </div>
                <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${statusClassName[client.status]}`}>
                  {statusLabel[client.status]}
                </span>
              </div>
            </div>
          ))
        ) : (
          <div className="rounded-xl border border-dashed border-gray-300 px-4 py-6 text-center text-sm text-gray-600 dark:border-white/10 dark:text-slate-400">
            No clients yet. Add one from the clients page to get started.
          </div>
        )}
      </div>
    </SectionCard>
  );
}
